/**
 * @module ClusterBriefingSummary
 * @purpose L1 AI summary region — renders ClusterBriefing summary paragraph + supported_points,
 *   or a muted "AI summary unavailable" note when the briefing is absent.
 * @depends types/reasoning, react-i18next
 * @dependedOnBy ReasoningClusterCard (L1 summary region)
 *
 * @rules
 * - Performance: pure display — all data passed via props; no data fetching
 * - State: none — stateless presentational component
 * - Errors: n/a — purely presentational; null briefing renders graceful fallback
 */

import { useTranslation } from 'react-i18next'
import type { ClusterBriefing } from '../../../../../types/reasoning'

interface ClusterBriefingSummaryProps {
  briefing: ClusterBriefing | null
  aiUnavailable: boolean
  loading: boolean
}

const SUMMARY_TEXT: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.82rem',
  color: 'var(--text)',
  lineHeight: '1.55',
  marginBottom: '8px',
}

const POINT_ITEM: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.78rem',
  color: 'var(--text-dim)',
  marginBottom: '3px',
  paddingLeft: '4px',
}

const UNAVAILABLE_NOTE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.72rem',
  color: 'var(--text-muted)',
  fontStyle: 'italic',
  padding: '6px 8px',
  borderRadius: '4px',
  background: 'transparent',
  border: '1px dashed var(--border)',
  display: 'inline-block',
}

const LOADING_INDICATOR: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.72rem',
  color: 'var(--text-muted)',
}

function SupportedPoints({ points }: { points: string[] }): React.ReactElement {
  const { t } = useTranslation('analysis')
  if (points.length === 0) return <></>
  return (
    <ul
      aria-label={t('reasoning.briefingBoard.summary.supportedPointsLabel' as never)}
      style={{ listStyle: 'disc', paddingLeft: '16px', margin: 0 }}
    >
      {points.map((point) => (
        <li key={point} style={POINT_ITEM}>
          {point}
        </li>
      ))}
    </ul>
  )
}

/**
 * Renders the L1 AI briefing summary region.
 *
 * @behaviour When loading, shows a loading indicator in the summary region.
 *   When briefing is present, renders summary paragraph + supported_points list.
 *   When briefing is null (AI unavailable or not generated), shows a muted note.
 *   Never throws regardless of input state.
 * @errors n/a — all states handled; null briefing → graceful fallback note
 */
export function ClusterBriefingSummary({
  briefing,
  aiUnavailable,
  loading,
}: ClusterBriefingSummaryProps): React.ReactElement {
  const { t } = useTranslation('analysis')

  if (loading) {
    return (
      <div
        role="status"
        aria-busy="true"
        style={LOADING_INDICATOR}
      >
        {t('reasoning.briefingBoard.summary.loading' as never)}
      </div>
    )
  }

  if (briefing !== null) {
    return (
      <div>
        <p style={SUMMARY_TEXT}>{briefing.summary}</p>
        <SupportedPoints points={briefing.supported_points} />
      </div>
    )
  }

  const noteKey = aiUnavailable
    ? ('reasoning.briefingBoard.summary.aiUnavailableNote' as never)
    : ('reasoning.briefingBoard.summary.notGeneratedNote' as never)

  return (
    <div>
      <span style={UNAVAILABLE_NOTE} role="note">
        {t(noteKey)}
      </span>
    </div>
  )
}
